import { useCallback, useEffect, useRef, useState } from "react";
import {
  Bold,
  Heading1,
  Heading2,
  Italic,
  List,
  ListOrdered,
  Quote,
  Redo,
  Underline as UnderlineIcon,
  Undo,
} from "lucide-react";

import { Button } from "@/components/ui/button";

interface LetterEditorProps {
  /** The complete letter HTML (with <head>/<style>/<body>) — preserved
   * exactly. Editing happens in an iframe in ``designMode``, so the
   * letter's typography, spacing, signature blocks, tables, anything
   * the LLM emitted, all round-trip unchanged. */
  initialHtml: string;
  onSave: (html: string) => void;
  onCancel: () => void;
  saving?: boolean;
}

interface ActiveFormats {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  h1: boolean;
  h2: boolean;
  blockquote: boolean;
  bulletList: boolean;
  orderedList: boolean;
}

const INITIAL_ACTIVE: ActiveFormats = {
  bold: false,
  italic: false,
  underline: false,
  h1: false,
  h2: false,
  blockquote: false,
  bulletList: false,
  orderedList: false,
};

export function LetterEditor({ initialHtml, onSave, onCancel, saving }: LetterEditorProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [ready, setReady] = useState(false);
  const [active, setActive] = useState<ActiveFormats>(INITIAL_ACTIVE);

  const updateActive = useCallback(() => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    const block = doc.queryCommandValue("formatBlock").toString().toLowerCase();
    setActive({
      bold: doc.queryCommandState("bold"),
      italic: doc.queryCommandState("italic"),
      underline: doc.queryCommandState("underline"),
      h1: block === "h1",
      h2: block === "h2",
      blockquote: block === "blockquote",
      bulletList: doc.queryCommandState("insertUnorderedList"),
      orderedList: doc.queryCommandState("insertOrderedList"),
    });
  }, []);

  const onIframeLoad = useCallback(() => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    doc.designMode = "on";
    doc.addEventListener("selectionchange", updateActive);
    doc.addEventListener("keyup", updateActive);
    doc.addEventListener("mouseup", updateActive);
    // Focus into the document so the caret lands in the letter
    // immediately when entering Edit mode.
    iframeRef.current?.contentWindow?.focus();
    setReady(true);
  }, [updateActive]);

  useEffect(() => {
    return () => {
      const doc = iframeRef.current?.contentDocument;
      if (!doc) return;
      doc.removeEventListener("selectionchange", updateActive);
      doc.removeEventListener("keyup", updateActive);
      doc.removeEventListener("mouseup", updateActive);
    };
  }, [updateActive]);

  function exec(command: string, value?: string) {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    doc.execCommand(command, false, value);
    iframeRef.current?.contentWindow?.focus();
    updateActive();
  }

  function toggleBlock(tag: "h1" | "h2" | "blockquote" | "p") {
    // execCommand wants formatBlock with the tag name; wrap in <> so
    // older WebKit accepts it.
    exec("formatBlock", `<${tag}>`);
  }

  function getFullHtml(): string {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return initialHtml;
    return "<!doctype html>" + doc.documentElement.outerHTML;
  }

  return (
    <div className="bg-slate-50 overflow-y-auto">
      <div className="px-6 py-4">
        <div>
          <div className="sticky top-0 z-10 bg-white border border-b-0 rounded-t-lg p-2 flex items-center justify-between gap-2 shadow-sm">
            <div className="flex items-center gap-0.5 flex-wrap">
              <ToolbarButton
                title="Bold"
                onClick={() => exec("bold")}
                active={active.bold}
                disabled={!ready}
              >
                <Bold className="w-3.5 h-3.5" />
              </ToolbarButton>
              <ToolbarButton
                title="Italic"
                onClick={() => exec("italic")}
                active={active.italic}
                disabled={!ready}
              >
                <Italic className="w-3.5 h-3.5" />
              </ToolbarButton>
              <ToolbarButton
                title="Underline"
                onClick={() => exec("underline")}
                active={active.underline}
                disabled={!ready}
              >
                <UnderlineIcon className="w-3.5 h-3.5" />
              </ToolbarButton>
              <div className="w-px h-4 bg-slate-200 mx-1" />
              <ToolbarButton
                title="Heading 1"
                onClick={() => toggleBlock(active.h1 ? "p" : "h1")}
                active={active.h1}
                disabled={!ready}
              >
                <Heading1 className="w-3.5 h-3.5" />
              </ToolbarButton>
              <ToolbarButton
                title="Heading 2"
                onClick={() => toggleBlock(active.h2 ? "p" : "h2")}
                active={active.h2}
                disabled={!ready}
              >
                <Heading2 className="w-3.5 h-3.5" />
              </ToolbarButton>
              <ToolbarButton
                title="Quote"
                onClick={() => toggleBlock(active.blockquote ? "p" : "blockquote")}
                active={active.blockquote}
                disabled={!ready}
              >
                <Quote className="w-3.5 h-3.5" />
              </ToolbarButton>
              <div className="w-px h-4 bg-slate-200 mx-1" />
              <ToolbarButton
                title="Bullet list"
                onClick={() => exec("insertUnorderedList")}
                active={active.bulletList}
                disabled={!ready}
              >
                <List className="w-3.5 h-3.5" />
              </ToolbarButton>
              <ToolbarButton
                title="Numbered list"
                onClick={() => exec("insertOrderedList")}
                active={active.orderedList}
                disabled={!ready}
              >
                <ListOrdered className="w-3.5 h-3.5" />
              </ToolbarButton>
              <div className="w-px h-4 bg-slate-200 mx-1" />
              <ToolbarButton title="Undo" onClick={() => exec("undo")} disabled={!ready}>
                <Undo className="w-3.5 h-3.5" />
              </ToolbarButton>
              <ToolbarButton title="Redo" onClick={() => exec("redo")} disabled={!ready}>
                <Redo className="w-3.5 h-3.5" />
              </ToolbarButton>
            </div>
            <div className="flex items-center gap-1.5">
              <Button size="sm" variant="outline" onClick={onCancel} disabled={saving}>
                Cancel
              </Button>
              <Button size="sm" onClick={() => onSave(getFullHtml())} disabled={saving || !ready}>
                {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
          <div className="border border-t-0 rounded-b-lg shadow-md overflow-hidden bg-white">
            <iframe
              ref={iframeRef}
              title="Appeal letter editor"
              srcDoc={initialHtml}
              onLoad={onIframeLoad}
              className="w-full"
              style={{ height: "calc(100vh - var(--chrome-h, 56px) - 184px)" }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function ToolbarButton({
  children,
  onClick,
  active,
  disabled,
  title,
}: {
  children: React.ReactNode;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
}) {
  return (
    <button
      type="button"
      // Critical: prevent the toolbar button from stealing focus from
      // the iframe. Without this, clicking the button collapses the
      // selection in the iframe document and execCommand applies to
      // nothing.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-pressed={active}
      className={
        "inline-flex items-center justify-center w-7 h-7 rounded transition " +
        (active ? "bg-slate-200 text-slate-900 " : "text-slate-600 ") +
        "hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-transparent"
      }
    >
      {children}
    </button>
  );
}
